import { Scene, AbstractMesh, Observable, PickingInfo, Vector3 } from '@babylonjs/core';
import ObservablesManager from './ObservablesManager';
import { MeshAssembly } from '../MeshAssembly';
import { PointerHit } from '../../interfaces';

export default class PointerManager {
	private scene: Scene;
	private observablesManager: ObservablesManager;
	private pickableMeshes: Map<AbstractMesh, Array<string>>;

	constructor(scene: Scene, observablesManager: ObservablesManager) {
		this.scene = scene;
		this.observablesManager = observablesManager;
		this.pickableMeshes = new Map<AbstractMesh, Array<string>>();
	}

	/**
	 * Will notify when eny input event happend.
	 */
	public get onPointerPick(): Observable<PickingInfo> {
		return this.observablesManager.onPointerPickObservable;
	}

	public registerPickable(mesh: AbstractMesh, reporter: string): void {
		if (this.pickableMeshes.has(mesh)) {
			(this.pickableMeshes.get(mesh) as string[]).push(reporter);
		} else {
			this.pickableMeshes.set(mesh, [reporter]);
		}
	}

	public unregisterPickable(mesh: AbstractMesh, reporter: string): void {
		if (this.pickableMeshes.has(mesh)) {
			const reporters = this.pickableMeshes.get(mesh);
			if (reporters) {
				const i = reporters.indexOf(reporter);
				if (i > -1) {
					reporters.splice(i);
				}
				if (reporters.length === 0) {
					this.pickableMeshes.delete(mesh);
				}
			}
		}
	}

	public getPickableReporters(mesh: AbstractMesh): Array<string> {
		const reporters = new Array<string>();
		const pickableMesh = this.pickableMeshes.get(mesh);
		if (pickableMesh) {
			return pickableMesh;
		}
		return reporters;
	}

	/**
	 * Pick.
	 */
	public pointerPick(x: number, y: number, target?: MeshAssembly): PointerHit | false {
		if (target) {
			const targetMeshes: Array<AbstractMesh> = target.transformNode.getChildMeshes();

			const pick: PickingInfo | null = this.scene.pick(x, y, (mesh: AbstractMesh): boolean => {
				return targetMeshes.includes(mesh);
			});
			if (pick && pick.hit && pick.pickedPoint) {
				this.onPointerPick.notifyObservers(pick);
				const position: Vector3 = pick.pickedPoint;
				return {
					hits: [target.name],
					position: {
						x: position.x,
						y: position.y,
						z: position.z,
					},
				};
			}
			return false;
		}

		const pick: PickingInfo | null = this.scene.pick(x, y);
		if (
			pick &&
			pick.hit &&
			pick.pickedPoint &&
			pick.pickedMesh &&
			this.pickableMeshes.has(pick.pickedMesh)
		) {
			this.onPointerPick.notifyObservers(pick);
			const position: Vector3 = pick.pickedPoint;
			return {
				hits: this.pickableMeshes.get(pick.pickedMesh) as string[],
				position: position
					? {
							x: position.x,
							y: position.y,
							z: position.z,
					  }
					: {},
			};
		}
		return false;
	}
}
